import React, { useState } from 'react'
import { Outlet, useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { useTheme } from '../context/ThemeContext'

export default function Banking() {
  const { user } = useAuth()
  const { isDark } = useTheme()
  const navigate = useNavigate()
  const location = useLocation()

  // Determine active tab from current route
  const getActiveTab = () => {
    const path = location.pathname
    if (path === '/banking' || path === '/banking/') return 'overview'
    if (path.includes('bank-statement')) return 'bank-statement'
    if (path.includes('account-transactions')) return 'account-transactions'
    if (path.includes('reconciliation')) return 'reconciliation'
    return 'overview'
  }

  const [activeTab, setActiveTab] = useState(getActiveTab())

  // Update active tab when route changes
  React.useEffect(() => {
    setActiveTab(getActiveTab())
  }, [location.pathname])

  const bankingItems = [
    { path: '/banking', label: 'Overview', icon: '🏦' },
    { path: '/banking/bank-statement', label: 'Bank Statement', icon: '📄' },
    { path: '/banking/account-transactions', label: 'Account Transactions', icon: '💰' },
    { path: '/banking/reconciliation', label: 'Reconciliation', icon: '🔄' }
  ]

  const handleTabClick = (path) => {
    setActiveTab(path)
    navigate(path)
  }

  const bgColor = isDark ? 'bg-slate-900' : 'bg-white'
  const textColor = isDark ? 'text-white' : 'text-gray-800'
  const borderColor = isDark ? 'border-slate-700' : 'border-gray-200'
  const tabBgColor = isDark ? 'bg-slate-800' : 'bg-gray-50'

  // Check if we're on the main overview page
  const isOverviewPage = location.pathname === '/banking' || location.pathname === '/banking/'

  return (
    <div className={`min-h-screen ${bgColor}`}>
      {/* Header */}
      <div className="border-b border-gray-200 dark:border-slate-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className={`text-3xl font-bold ${textColor}`}>🏦 Banking</h1>
              <p className={`mt-1 text-sm ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>
                Manage bank statements, account transactions, and reconciliation
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className={`border-b ${borderColor}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-8" aria-label="Tabs">
            {bankingItems.map((item) => (
              <button
                key={item.path}
                onClick={() => handleTabClick(item.path)}
                className={`
                  py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2 transition-colors
                  ${activeTab === item.path || (item.path === '/banking' && isOverviewPage)
                    ? 'border-blue-500 text-blue-600'
                    : `border-transparent ${isDark ? 'text-slate-400 hover:text-slate-300' : 'text-gray-500 hover:text-gray-700'}`
                  }
                `}
              >
                <span className="text-lg">{item.icon}</span>
                {item.label}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Content Area - Outlet for nested routes or overview content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {isOverviewPage ? (
          <div className={`${bgColor} rounded-lg shadow-sm border ${borderColor} p-6`}>
            <div className="space-y-6">
              <h2 className={`text-2xl font-bold ${textColor}`}>Banking Overview</h2>

              {/* Feature Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Bank Statement Card */}
                <div className={`${tabBgColor} rounded-lg p-6 border ${borderColor} hover:shadow-md transition-shadow cursor-pointer`}
                     onClick={() => handleTabClick('/banking/bank-statement')}>
                  <div className="flex items-start gap-4">
                    <div className="text-4xl">📄</div>
                    <div className="flex-1">
                      <h3 className={`text-lg font-semibold ${textColor}`}>Bank Statement</h3>
                      <p className={`mt-2 text-sm ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>
                        Upload bank statements and let AI extract transaction data automatically
                      </p>
                      <div className="mt-4 flex items-center text-blue-600 text-sm font-medium">
                        <span>Upload Statement</span>
                        <svg className="ml-2 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Account Transactions Card */}
                <div className={`${tabBgColor} rounded-lg p-6 border ${borderColor} hover:shadow-md transition-shadow cursor-pointer`}
                     onClick={() => handleTabClick('/banking/account-transactions')}>
                  <div className="flex items-start gap-4">
                    <div className="text-4xl">💰</div>
                    <div className="flex-1">
                      <h3 className={`text-lg font-semibold ${textColor}`}>Account Transactions</h3>
                      <p className={`mt-2 text-sm ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>
                        View and manage all bank transactions in a comprehensive table view
                      </p>
                      <div className="mt-4 flex items-center text-blue-600 text-sm font-medium">
                        <span>View Transactions</span>
                        <svg className="ml-2 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Reconciliation Card */}
                <div className={`${tabBgColor} rounded-lg p-6 border ${borderColor} hover:shadow-md transition-shadow cursor-pointer`}
                     onClick={() => handleTabClick('/banking/reconciliation')}>
                  <div className="flex items-start gap-4">
                    <div className="text-4xl">🔄</div>
                    <div className="flex-1">
                      <h3 className={`text-lg font-semibold ${textColor}`}>Reconciliation</h3>
                      <p className={`mt-2 text-sm ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>
                        Match bank statements with internal records and identify discrepancies
                      </p>
                      <div className="mt-4 flex items-center text-blue-600 text-sm font-medium">
                        <span>Start Reconciliation</span>
                        <svg className="ml-2 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Quick Stats */}
              <div className={`mt-8 ${tabBgColor} rounded-lg p-6 border ${borderColor}`}>
                <h3 className={`text-lg font-semibold ${textColor} mb-4`}>Quick Statistics</h3>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-600">RM 45,230</div>
                    <div className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>Total Bank Balance</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-blue-600">127</div>
                    <div className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>This Month Transactions</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-purple-600">3</div>
                    <div className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>Bank Accounts</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-orange-600">98.5%</div>
                    <div className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>Reconciliation Rate</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <Outlet />
        )}
      </div>
    </div>
  )
}
